import React from 'react'
import { useParams } from 'react-router-dom'

import { ColorGuessing } from '../components/ColorGuessing'
import { useForceUpdate } from '../hooks/useForceUpdate'
import { WebRTCRole, WebRTCStatus } from '../library/web-rtc/WebRTC'
import { useWebRTC } from '../library/web-rtc/useWebRTC'

export function PartyPage() {
  const webRTC = useWebRTC()
  const fU = useForceUpdate()

  const forceUpdate = React.useCallback(
    (event: any) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(event)
      }
      fU()
    },
    [fU]
  )

  React.useEffect(() => {
    webRTC.signaling.on('*', forceUpdate)
    webRTC.on('*', forceUpdate)

    return () => {
      webRTC.signaling.off('*', forceUpdate)
      webRTC.off('*', forceUpdate)
    }
  }, [webRTC])

  switch (webRTC.status) {
    case WebRTCStatus.IDLE:
      return <BeforeJoining />
    case WebRTCStatus.LOBBY:
      return <LobbyPage />
    case WebRTCStatus.PLAYING:
      return <PlayingPage />
  }
}

function BeforeJoining() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [userName, setUserName] = React.useState('')
  const webRTC = useWebRTC()

  if (!sessionId) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="card text-center">
          <h2 className="text-2xl font-bold text-red-600">No session ID</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center px-2 sm:px-4">
      <div className="card max-w-md w-full animate-scale-in text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 md:mb-8">
          <span className="rainbow-gradient-text">Shade Parades</span>
        </h1>
        <div className="space-y-3 sm:space-y-4">
          <input
            name="name"
            placeholder="Enter your name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && userName && webRTC.joinSession(sessionId, { name: userName })}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base md:text-lg rounded-lg border-2 border-gray-300 focus:outline-none transition-colors duration-300"
            autoFocus
          />
          <button onClick={() => webRTC.joinSession(sessionId, { name: userName })} disabled={!userName} className="w-full btn-primary text-base sm:text-lg">
            Join Game
          </button>
        </div>
      </div>
    </div>
  )
}

function LobbyPage() {
  const webRTC = useWebRTC()

  switch (webRTC.role) {
    case WebRTCRole.HOST:
      return <HostLobbyPage />
    case WebRTCRole.CLIENT:
      return <ClientLobbyPage />
  }

  return null
}

function HostLobbyPage() {
  const webRTC = useWebRTC()
  const [userName, setUserName] = React.useState(webRTC.myMetadata?.name || '')
  const [copySuccess, setCopySuccess] = React.useState(false)
  const forceUpdate = useForceUpdate()

  React.useEffect(() => {
    webRTC.signaling.on('*', forceUpdate)
    webRTC.on('*', forceUpdate)

    return () => {
      webRTC.signaling.off('*', forceUpdate)
      webRTC.off('*', forceUpdate)
    }
  }, [webRTC])

  const onNameChange = () => {
    webRTC.updateMetadata({ name: userName })
  }

  const copyPartyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = window.location.href
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }

  return (
    <div className="min-h-screen gradient-bg px-2 sm:px-4 py-4 sm:py-6 md:py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-4 sm:mb-6 md:mb-8 drop-shadow-lg animate-slide-up">
          🎨 <span className="rainbow-gradient-text">Shade Parades</span>
        </h1>

        <div className="card mb-3 sm:mb-4 md:mb-6 animate-scale-in">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">Host Lobby</h2>
          <div>
            <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Your Name</label>
            <input
              name="name"
              placeholder="Enter your name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onBlur={onNameChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-300 focus:outline-none transition-colors duration-300"
            />
          </div>

          <div className="mt-4">
            <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Party Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={window.location.href}
                readOnly
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-300 bg-gray-50 text-gray-600"
              />
              <button
                onClick={copyPartyLink}
                className={`px-4 py-2 sm:py-3 text-sm sm:text-base font-semibold rounded-lg transition-all duration-300 ${
                  copySuccess ? 'bg-green-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {copySuccess ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Share this link with friends to invite them to your party!</p>
          </div>
        </div>

        <div className="card mb-3 sm:mb-4 md:mb-6 animate-fade-in">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">Connected Players</h2>
          <div className="space-y-2 sm:space-y-3">
            {userName && (
              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-lg shadow-md">
                <span className="text-xl sm:text-2xl">👑</span>
                <span className="font-semibold text-gray-800 text-sm sm:text-base">{userName}</span>
                <span className="text-xs sm:text-sm text-gray-600">(You - Host)</span>
              </div>
            )}
            {Object.values(webRTC.clients).map((client) => (
              <div key={client.clientId} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 bg-gray-100 rounded-lg shadow-md">
                <span className="text-xl sm:text-2xl">👤</span>
                <span className="font-semibold text-gray-800 text-sm sm:text-base">{client.metadata?.name || 'Unknown'}</span>
              </div>
            ))}
          </div>

          {Object.values(webRTC.clients).length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Waiting for players to join...</p>
              <div className="inline-block w-12 h-12 rounded-full animate-spin rainbow-spinner"></div>
            </div>
          )}
        </div>

        {Object.values(webRTC.clients).length >= 1 && (
          <button onClick={() => webRTC.completeSession()} className="w-full btn-primary text-base sm:text-lg animate-bounce-subtle">
            Start Game
          </button>
        )}
      </div>
    </div>
  )
}

function ClientLobbyPage() {
  const webRTC = useWebRTC()
  const [userName, setUserName] = React.useState(webRTC.myMetadata?.name || '')
  const forceUpdate = useForceUpdate()

  React.useEffect(() => {
    webRTC.signaling.on('*', forceUpdate)
    webRTC.on('*', forceUpdate)

    return () => {
      webRTC.signaling.off('*', forceUpdate)
      webRTC.off('*', forceUpdate)
    }
  }, [webRTC])

  const onNameChange = () => {
    webRTC.updateMetadata({ name: userName })
  }

  return (
    <div className="min-h-screen gradient-bg px-2 sm:px-4 py-4 sm:py-6 md:py-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-4 sm:mb-6 md:mb-8 drop-shadow-lg animate-slide-up">
          🎨 <span className="rainbow-gradient-text">Shade Parades</span>
        </h1>

        <div className="card mb-3 sm:mb-4 md:mb-6 animate-scale-in">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">Player Lobby</h2>
          <div>
            <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Your Name</label>
            <input
              name="name"
              placeholder="Enter your name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onBlur={onNameChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-300 focus:outline-none transition-colors duration-300"
            />
          </div>
        </div>

        <div className="card mb-3 sm:mb-4 md:mb-6 animate-fade-in">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">Connected Players</h2>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-lg shadow-md">
              <span className="text-xl sm:text-2xl">👑</span>
              <span className="font-semibold text-gray-800 text-sm sm:text-base">{webRTC.host?.metadata?.name || 'Unknown'}</span>
              <span className="text-xs sm:text-sm text-gray-600">(Host)</span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 bg-gradient-to-r from-blue-100 to-blue-200 rounded-lg shadow-md">
              <span className="text-xl sm:text-2xl">👤</span>
              <span className="font-semibold text-gray-800 text-sm sm:text-base">{webRTC.myMetadata?.name || 'Unknown'}</span>
              <span className="text-xs sm:text-sm text-gray-600">(You)</span>
            </div>

            {Object.values(webRTC.status === WebRTCStatus.PLAYING ? webRTC.meshClients : webRTC.priorityClients.filter((client) => client.clientId !== webRTC.myClientId)).map(
              (client) => (
                <div key={client.clientId} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 bg-gray-100 rounded-lg shadow-md">
                  <span className="text-xl sm:text-2xl">👤</span>
                  <span className="font-semibold text-gray-800 text-sm sm:text-base">{client.metadata?.name || 'Unknown'}</span>
                </div>
              )
            )}
          </div>
        </div>

        <div className="card text-center">
          <p className="text-gray-600 mb-4">Waiting for host to start the game...</p>
          <div className="inline-block w-12 h-12 rounded-full animate-spin rainbow-spinner"></div>
        </div>
      </div>
    </div>
  )
}

function PlayingPage() {
  return <ColorGuessing />
}
